import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, MessageSquare } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Sidebar from '../components/Sidebar';
import { tenantService } from '../services/tenantService';
import { propertyService } from '../services/propertyService';
import { chatService } from '../services/chatService';

const TenantsPage = () => {
  const { currentUser, userData } = useAuth();
  const { t } = useLanguage();
  const [tenants, setTenants] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (currentUser && userData) {
      console.log('Setting up tenants page for:', currentUser.uid);
      try {
        // Subscribe to tenants
        const tenantUnsubscribe = tenantService.subscribeToTenantAssignmentsByOwner(
          currentUser.uid,
          (tenants) => {
            console.log('Owner tenants received:', tenants);
            setTenants(tenants || []);
            setLoading(false);
          }
        );

        // Subscribe to properties for reference
        const propertyUnsubscribe = propertyService.subscribeToPropertiesByOwner(
          currentUser.uid,
          (properties) => {
            console.log('Owner properties received:', properties);
            setProperties(properties || []);
          }
        );

        return () => {
          tenantUnsubscribe();
          propertyUnsubscribe();
        };
      } catch (error) {
        console.error('Error setting up subscriptions:', error);
        setTenants([]);
        setProperties([]);
        setLoading(false);
      }
    } else {
      console.log('No currentUser or userData:', { currentUser, userData });
      setLoading(false);
    }
  }, [currentUser, userData]);



  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-slate-100 text-slate-700 border-slate-200';
      case 'terminated': return 'bg-slate-100 text-slate-600 border-slate-200';
      default: return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  const getPropertyName = (propertyId) => {
    const property = properties.find(p => p.id === propertyId);
    return property ? property.name : 'Unknown Property';
  };

  const handleStartChat = async (tenant) => {
    try {
      console.log('Starting chat with tenant:', tenant);
      console.log('Current user:', currentUser.uid);
      console.log('Available properties:', properties);
      
      const property = properties.find(p => p.id === tenant.propertyId);
      const propertyName = property ? property.name : 'Unknown Property';
      
      console.log('Found property:', property);
      console.log('Property name:', propertyName);
      
      // Create or get existing direct message conversation
      console.log('Creating direct message...');
      const conversationId = await chatService.createDirectMessage(
        currentUser.uid,
        tenant.tenantId,
        tenant.propertyId,
        propertyName
      );
      
      console.log('Conversation created with ID:', conversationId);
      
      // Navigate to chats page with the conversation
      window.location.href = `/chats?conversation=${conversationId}`;
    } catch (error) {
      console.error('Error starting chat:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      alert('Error starting chat. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-slate-200 border-t-slate-600 mx-auto" />
          <p className="mt-4 text-sm font-medium text-slate-600">Loading tenants...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="flex">
        <Sidebar />
        <div className="flex-1 ml-72 -ml-8">
          <header className="bg-white border-b border-slate-200 sticky top-0 z-10 w-full">
            <div className="flex items-center justify-between px-8 py-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-slate-600" strokeWidth={1.75} />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-slate-800">{t('dashboard.owner.pages.tenants.title')}</h1>
                  <p className="text-slate-500 text-sm">Manage tenant assignments and leases</p>
                </div>
              </div>
              <div className="text-sm text-slate-500 pl-3 border-l border-slate-200">
                {new Date().toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short' })}
              </div>
            </div>
          </header>

          <div className="p-8">
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-base font-semibold text-slate-800">{t('dashboard.owner.pages.tenants.yourTenants')}</h2>
                <span className="text-sm text-slate-500">{tenants.length} {tenants.length === 1 ? t('dashboard.owner.pages.tenants.tenant') : t('dashboard.owner.pages.tenants.tenants')}</span>
              </div>

              {tenants.length === 0 ? (
                <div className="text-center py-10">
                  <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                    <Users className="w-6 h-6 text-slate-400" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-base font-semibold text-slate-800 mb-2">No tenants yet</h3>
                  <p className="text-slate-500 mb-4 text-sm">Create tenant accounts from the dashboard or property details.</p>
                  <Link to="/dashboard" className="inline-flex items-center gap-2 px-4 py-2 bg-slate-700 text-white text-sm font-medium rounded-md hover:bg-slate-800 transition-colors">
                    Go to Dashboard
                  </Link>
                </div>
              ) : (
                <div className="space-y-3">
                  {tenants.map((tenant) => (
                    <div key={tenant.id} className="flex items-center gap-4 p-4 bg-slate-50 rounded-md border border-slate-200">
                      <div className="w-10 h-10 bg-slate-200 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Users className="w-5 h-5 text-slate-600" strokeWidth={1.75} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-semibold text-slate-800">{tenant.tenantName}</h3>
                        <p className="text-xs text-slate-500 truncate">{tenant.tenantEmail}</p>
                        {tenant.phone && <p className="text-xs text-slate-400">{t('dashboard.owner.pages.tenants.phone')}: {tenant.phone}</p>}
                      </div>
                      <div className="text-right text-sm">
                        <p className="font-medium text-slate-800">{getPropertyName(tenant.propertyId)}</p>
                        <p className="text-xs text-slate-500">{t('dashboard.owner.pages.tenants.rentAmount')}: ${tenant.monthlyRent}</p>
                        <p className="text-xs text-slate-400">
                          {new Date(tenant.leaseStartDate).toLocaleDateString()} – {new Date(tenant.leaseEndDate).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded border ${getStatusColor(tenant.status)}`}>{tenant.status}</span>
                        <button
                          onClick={() => handleStartChat(tenant)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-700 text-white text-sm font-medium rounded-md hover:bg-slate-800 transition-colors"
                        >
                          <MessageSquare className="w-4 h-4" strokeWidth={1.75} />
                          Message
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TenantsPage; 